import {
  OrderOrderByWithRelationInput,
  OrdersQuery,
  OrderStatus,
  OrderWhereInput,
  PaymentStatus,
  QueryMode,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  DefaultMantineColor,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue, useInputState, useLocalStorage } from '@mantine/hooks';
import { modals, openConfirmModal } from '@mantine/modals';
import {
  IconExclamationMark,
  IconEye,
  IconInvoice,
  IconPlus,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OrdersSummary } from '../components/OrdersSummary';
import {
  cancelOrderDocument,
  issueInvoiceDocument,
  sendMessagesDocument,
} from '../graphql/mutations';
import { countOrdersDocument, ordersDocument } from '../graphql/queries';
import { productLivesDocument } from '@/features/product/graphql/get-products';

const orderStatusTranslations = {
  [OrderStatus.Created]: 'Vytvorená',
  [OrderStatus.Confirmed]: 'Potvrdená',
  [OrderStatus.Processed]: 'Spracovaná',
  [OrderStatus.Shipped]: 'Odoslaná',
  [OrderStatus.Delivered]: 'Doručená',
  [OrderStatus.Cancelled]: 'Zrušená',
  [OrderStatus.Reviewing]: 'V riešení',
  [OrderStatus.Returned]: 'Vrátená',
  [OrderStatus.Internal]: 'Interná',
  [OrderStatus.Reserved]: 'Rezervovaná',
};

const orderStatusColors: Partial<Record<OrderStatus, DefaultMantineColor>> = {
  [OrderStatus.Created]: 'gray',
  [OrderStatus.Confirmed]: 'blue',
  [OrderStatus.Processed]: 'cyan',
  [OrderStatus.Shipped]: 'teal',
  [OrderStatus.Reviewing]: 'orange',
  [OrderStatus.Delivered]: 'green',
  [OrderStatus.Cancelled]: 'red',
  [OrderStatus.Returned]: 'gray',
  [OrderStatus.Internal]: 'dark',
  [OrderStatus.Reserved]: 'violet',
};

const paymentMethodColors: Record<string, DefaultMantineColor> = {
  Prevod: 'blue',
  Dobierka: 'orange',
  Karta: 'teal',
  Osobne: 'indigo',
};

const paymentStatusTranslations = {
  [PaymentStatus.Pending]: 'Čakajúca',
  [PaymentStatus.Review]: 'Nespárovaná',
  [PaymentStatus.Failed]: 'Zlyhala',
  [PaymentStatus.Completed]: 'Zaplatená',
};

const paymentStatusColors = {
  [PaymentStatus.Pending]: 'gray',
  [PaymentStatus.Review]: 'orange',
  [PaymentStatus.Failed]: 'red',
  [PaymentStatus.Completed]: 'green',
};

const SendMessagesModal = ({ orderIds }: { orderIds: string[] }) => {
  const [message, setMessage] = useInputState('');

  const [sendMessages, { loading }] = useMutation(sendMessagesDocument, {
    refetchQueries: 'all',
    awaitRefetchQueries: true,
  });

  return (
    <Stack>
      <TextInput value={message} onChange={setMessage} placeholder="Správa..." />
      <Button
        disabled={message.length === 0}
        loading={loading}
        onClick={async () => {
          await sendMessages({ variables: { orderIds, message } });

          modals.closeAll();
        }}
      >
        Odoslať ({orderIds.length})
      </Button>
    </Stack>
  );
};

const EmployeesModal = ({ liveId, payment }: { liveId: string; payment?: string }) => {
  const [employees, setEmployees] = useLocalStorage<string[]>({
    key: 'employees',
    defaultValue: [],
  });

  const [notPacked, setNotPacked] = useLocalStorage<boolean>({
    key: 'notPacked',
    defaultValue: true,
  });

  return (
    <Stack>
      <Alert c="orange">Zadaj mená zamestnancov, ktorí budú baliť objednávky.</Alert>
      {!payment && <Alert c="red">POZOR! Tlač nezaplatených objednávok!</Alert>}
      <TagsInput
        value={employees}
        onChange={setEmployees}
        title="Zamestnanci"
        description="Napíš meno a stlač 'Enter'"
      />
      {payment && (
        <Checkbox
          checked={notPacked}
          onChange={(e) => setNotPacked(e.currentTarget.checked)}
          label="Vytlačiť len nezabalené?"
        />
      )}
      {payment ? (
        <Button
          disabled={employees.length === 0}
          component="a"
          href={`https://api.laskykrasky.sk/api/order?${new URLSearchParams({
            liveId,
            payment,
            employees: employees.join(','),
            notPacked: notPacked.toString(),
          })}`}
          target="_blank"
        >
          Vytlačiť
        </Button>
      ) : (
        <Button
          c="orange"
          disabled={employees.length === 0}
          component="a"
          href={`https://api.laskykrasky.sk/api/order/unpaid?${new URLSearchParams({
            liveId,
            employees: employees.join(','),
          })}`}
          target="_blank"
        >
          Vytlačiť nezaplatené
        </Button>
      )}
    </Stack>
  );
};

export const OrderList = () => {
  const navigate = useNavigate();

  const [filterStatus, setFilterStatusInternal] = useLocalStorage<OrderWhereInput>({
    key: 'order-list-filter',
    defaultValue: {},
  });

  const setFilterStatus: typeof setFilterStatusInternal = (filterVal) => {
    setFilterStatusInternal(filterVal);
    setPage(1);
  };

  const [filterStatusDebounced] = useDebouncedValue(filterStatus, 500);

  const [sortStatus, setSortStatus] = useLocalStorage<
    DataTableSortStatus<OrdersQuery['orders'][number]>
  >({
    key: 'order-list-sort',
    defaultValue: {
      columnAccessor: 'createdAt',
      direction: 'desc',
    },
  });

  const orderBy = useMemo<OrderOrderByWithRelationInput>(() => {
    switch (sortStatus.columnAccessor) {
      case 'customer.name':
        return { customer: { name: sortStatus.direction as SortOrder } };
      case 'live.name':
        return { live: { name: sortStatus.direction as SortOrder } };
      case 'shippingMethod.name':
        return { shippingMethod: { name: sortStatus.direction as SortOrder } };
      case 'paymentMethod.name':
        return { paymentMethod: { name: sortStatus.direction as SortOrder } };
      case 'packedBy':
        return { packedBy: { sort: sortStatus.direction as SortOrder } };
      case 'invoice':
        return { invoice: { number: sortStatus.direction as SortOrder } };
      default:
        return { [sortStatus.columnAccessor]: sortStatus.direction };
    }
  }, [sortStatus]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage({
    key: 'order-list-page-size',
    defaultValue: 25,
  });

  const [selectedOrders, setSelectedOrders] = useState<OrdersQuery['orders']>([]);

  const [cancelOrder, cancelOrderMutation] = useMutation(cancelOrderDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const { data: lives } = useQuery(productLivesDocument, {
    variables: {
      orderBy: { date: SortOrder.Desc },
    },
  });

  const { data: count } = useQuery(countOrdersDocument, {
    variables: { where: filterStatusDebounced },
    skip: Object.keys(filterStatusDebounced).length === 0,
  });

  const { data, loading } = useQuery(ordersDocument, {
    variables: {
      where: filterStatusDebounced,
      skip: pageSize * page - pageSize,
      take: pageSize,
      orderBy,
    },
    skip: Object.keys(filterStatusDebounced).length === 0,
  });

  const [issueInvoice] = useMutation(issueInvoiceDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const activeFilters: { label: string; color: DefaultMantineColor; onClear: () => void }[] = [];

  if (filterStatus.live?.is?.id?.equals) {
    const liveId = filterStatus.live.is.id.equals;
    const live = lives?.lives.find((l) => l.id === liveId);
    activeFilters.push({
      label: `LIVE: ${live?.name ?? liveId}`,
      color: 'grape', // arbitrary but distinct
      onClear: () => setFilterStatus({ ...filterStatus, live: undefined }),
    });
  }

  if (filterStatus.status?.equals) {
    const status = filterStatus.status.equals;
    activeFilters.push({
      label: `Stav: ${orderStatusTranslations[status]}`,
      color: orderStatusColors[status] ?? 'gray',
      onClear: () => setFilterStatus({ ...filterStatus, status: undefined }),
    });
  }

  if (filterStatus.paymentMethod?.is?.name?.equals) {
    const method = filterStatus.paymentMethod.is.name.equals;
    activeFilters.push({
      label: `Platba: ${method}`,
      color: paymentMethodColors[method] ?? 'gray',
      onClear: () => setFilterStatus({ ...filterStatus, paymentMethod: undefined }),
    });
  }

  if (filterStatus.payments?.some?.status?.equals) {
    const payStatus = filterStatus.payments.some.status.equals;
    activeFilters.push({
      label: `Stav platby: ${paymentStatusTranslations[payStatus]}`,
      color: paymentStatusColors[payStatus] ?? 'gray',
      onClear: () => setFilterStatus({ ...filterStatus, payments: undefined }),
    });
  }

  if (typeof filterStatus.package?.equals === 'number') {
    activeFilters.push({
      label: `Č. balíka: ${filterStatus.package.equals}`,
      color: 'violet',
      onClear: () => setFilterStatus({ ...filterStatus, package: undefined }),
    });
  }

  if (filterStatus.customer?.is?.name?.contains) {
    activeFilters.push({
      label: `Zákazník: ${filterStatus.customer.is.name.contains}`,
      color: 'blue',
      onClear: () => setFilterStatus({ ...filterStatus, customer: undefined }),
    });
  }

  return (
    <Stack>
      <Paper p="md" withBorder component={Stack}>
        <Select
          clearable
          placeholder="LIVE..."
          data={lives?.lives.map((live) => ({ value: live.id, label: live.name })) ?? []}
          value={filterStatus.live?.is?.id?.equals ?? ''}
          onChange={(value) => {
            setFilterStatus((prev) => ({
              ...prev,
              live: value ? { is: { id: { equals: value } } } : undefined,
            }));
          }}
          onClear={() => {
            setFilterStatus((prev) => ({ ...prev, live: undefined }));
          }}
          error={!filterStatus.live?.is?.id?.equals}
          flex={1}
        />
        <Group>
          <Text fw={500}>Stav objednávky</Text>
          {Object.entries(orderStatusColors).map(([status, color]) => (
            <Button
              size="sm"
              key={status}
              color={color}
              disabled={filterStatus.status?.equals === status}
              onClick={() => {
                setFilterStatus((prev) => ({
                  ...prev,
                  status: { equals: status as OrderStatus },
                }));
              }}
            >
              {orderStatusTranslations[status as OrderStatus]}
            </Button>
          ))}
          <ActionIcon
            size="lg"
            variant="light"
            color="red"
            disabled={!filterStatus.status?.equals}
            onClick={() => setFilterStatus((prev) => ({ ...prev, status: undefined }))}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <Group>
          <Text fw={500}>Spôsob platby</Text>
          {Object.entries(paymentMethodColors).map(([paymentMethod, color]) => (
            <Button
              size="sm"
              key={paymentMethod}
              color={color}
              disabled={filterStatus.paymentMethod?.is?.name?.equals === paymentMethod}
              onClick={() => {
                setFilterStatus((prev) => ({
                  ...prev,
                  paymentMethod: { is: { name: { equals: paymentMethod } } },
                }));
              }}
            >
              {paymentMethod}
            </Button>
          ))}
          <ActionIcon
            size="lg"
            variant="light"
            color="red"
            disabled={!filterStatus.paymentMethod?.is?.name?.equals}
            onClick={() => setFilterStatus((prev) => ({ ...prev, paymentMethod: undefined }))}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <Group>
          <Text fw={500}>Stav platby</Text>
          {Object.entries(paymentStatusColors).map(([status, color]) => (
            <Button
              size="sm"
              key={status}
              color={color}
              disabled={filterStatus.payments?.some?.status?.equals === status}
              onClick={() => {
                setFilterStatus((prev) => ({
                  ...prev,
                  payments: { some: { status: { equals: status as PaymentStatus } } },
                }));
              }}
            >
              {paymentStatusTranslations[status as PaymentStatus]}
            </Button>
          ))}
          <ActionIcon
            size="lg"
            color="red"
            variant="light"
            disabled={!filterStatus.payments?.some?.status?.equals}
            onClick={() => setFilterStatus((prev) => ({ ...prev, payments: undefined }))}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <Group justify="space-between">
          <Group>
            <NumberInput
              placeholder="Č. balíka..."
              value={filterStatus.package?.equals ?? ''}
              onChange={(value) => {
                setFilterStatus((prev) => ({
                  ...prev,
                  package: value !== '' ? { equals: Number(value) } : undefined,
                }));
              }}
              allowNegative={false}
              allowDecimal={false}
              hideControls
            />
            <TextInput
              placeholder="Meno zákazníka..."
              value={filterStatus.customer?.is?.name?.contains ?? ''}
              onChange={(event) => {
                setFilterStatus((prev) => ({
                  ...prev,
                  customer:
                    event.currentTarget.value !== ''
                      ? {
                          is: {
                            name: { contains: event.target.value, mode: QueryMode.Insensitive },
                          },
                        }
                      : undefined,
                }));
              }}
            />
          </Group>
          <Group>
            {filterStatus.live?.is?.id?.equals && filterStatus.paymentMethod?.is?.name?.equals && (
              <Button
                color={paymentMethodColors[filterStatus.paymentMethod.is.name.equals]}
                onClick={() =>
                  filterStatus.live?.is?.id?.equals &&
                  filterStatus.paymentMethod?.is?.name?.equals &&
                  modals.open({
                    title: 'Vytlačiť objednávky',
                    children: (
                      <EmployeesModal
                        liveId={filterStatus.live?.is?.id?.equals}
                        payment={filterStatus.paymentMethod?.is?.name?.equals}
                      />
                    ),
                  })
                }
              >
                Vytlačiť ({filterStatus.paymentMethod.is.name.equals})
              </Button>
            )}
            {filterStatus.live?.is?.id?.equals &&
              filterStatus.status?.equals === OrderStatus.Confirmed && (
                <Button
                  color="orange"
                  variant="light"
                  onClick={() =>
                    filterStatus.live?.is?.id?.equals &&
                    filterStatus.status?.equals === OrderStatus.Confirmed &&
                    modals.open({
                      title: 'Vytlačiť nezaplatené objednávky',
                      children: <EmployeesModal liveId={filterStatus.live?.is?.id?.equals} />,
                    })
                  }
                  rightSection={<IconExclamationMark size={16} />}
                >
                  Vytlačiť nezaplatené
                </Button>
              )}
            {/* {filterStatus.paymentMethod?.is?.name?.equals === 'Osobne' &&
              filterStatus.live?.is?.id?.equals && (
                <Button
                  component="a"
                  href={`https://api.laskykrasky.sk/api/order/pickup?liveId=${filterStatus.live.is.id.equals}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Vytlačiť osobný zoznam
                </Button>
              )} */}
            {selectedOrders.length > 0 && (
              <Button
                color="red"
                loading={cancelOrderMutation.loading}
                onClick={() => {
                  modals.openConfirmModal({
                    title: `Zrušiť objednávky ${selectedOrders.length}`,
                    children: (
                      <Stack>
                        <Alert color="red">Naozaj chcete zrušiť tieto objednávky?</Alert>
                        <Text>
                          {selectedOrders
                            .map((order) => `${order.package} - ${order.customer.name}`)
                            .join(', ')}
                        </Text>
                      </Stack>
                    ),
                    onConfirm: async () => {
                      await Promise.allSettled(
                        selectedOrders.map((order) =>
                          cancelOrder({ variables: { orderId: order.id } })
                        )
                      );
                    },
                    labels: { cancel: 'Nie', confirm: 'Áno' },
                  });
                }}
              >
                Zrušiť {selectedOrders.length} objednávok
              </Button>
            )}
            {filterStatus.live?.is?.id?.equals && (
              <ActionIcon
                color="cyan"
                size="input-sm"
                onClick={() =>
                  modals.open({
                    title: 'Odoslať správu',
                    children: (
                      <SendMessagesModal orderIds={data?.orders.map((order) => order.id) ?? []} />
                    ),
                  })
                }
              >
                <IconSend size={16} />
              </ActionIcon>
            )}
          </Group>
        </Group>
        {activeFilters.length > 0 && (
          <Group>
            <Text size="sm" fw={500} c="dimmed">
              Aktívne filtre:
            </Text>
            {activeFilters.map((filter, index) => (
              <Badge
                key={index}
                color={filter.color}
                variant="outline"
                size="lg"
                radius="xs"
                onClick={filter.onClear}
                style={{ cursor: 'pointer' }}
              >
                {filter.label}
              </Badge>
            ))}
            <ActionIcon
              variant="outline"
              color="red"
              onClick={() =>
                setFilterStatus((prev) => ({
                  live: prev.live,
                }))
              }
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        )}
      </Paper>
      {filterStatus.live?.is?.id?.equals && (
        <OrdersSummary liveId={filterStatus.live.is.id.equals} />
      )}
      <DataTable
        withTableBorder
        striped
        highlightOnHover
        textSelectionDisabled
        borderRadius="sm"
        minHeight={200}
        records={data?.orders ?? []}
        fetching={loading}
        totalRecords={count?.countOrders ?? 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={setPage}
        recordsPerPageOptions={[5, 10, 25, 50, 75, 100]}
        onRecordsPerPageChange={setPageSize}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        selectedRecords={selectedOrders}
        onSelectedRecordsChange={setSelectedOrders}
        columns={[
          {
            accessor: 'package',
            title: '#',
            render: ({ id, package: packageNumber }) => (
              <Anchor inherit to={id} component={Link}>
                {packageNumber}
              </Anchor>
            ),
            sortable: true,
          },
          {
            accessor: 'status',
            title: 'Stav',
            sortable: true,
            render: ({ status }) => (
              <Badge color={orderStatusColors[status]}>{orderStatusTranslations[status]}</Badge>
            ),
          },
          {
            accessor: 'customer.name',
            title: 'Zákazník',
            render: ({ customer }) =>
              `${customer.name}${customer.metadata?.ibanType ? ` (${customer.metadata?.ibanType})` : ''}`,
            sortable: true,
          },
          {
            accessor: 'live.name',
            title: 'LIVE',
            sortable: true,
          },

          {
            accessor: 'total',
            title: 'Spolu',
            sortable: true,
            render: ({ total }) => `${total} €`,
          },
          {
            accessor: 'shippingMethod.name',
            title: 'Preprava',
            sortable: true,
          },
          {
            accessor: 'paymentMethod.name',
            title: 'Platba',
            sortable: true,
          },
          {
            accessor: 'payments',
            title: 'Stav',
            render: ({ payments }) => {
              const payment = payments?.at(0);

              if (!payment) return null;

              return (
                <Group>
                  <Badge color={paymentStatusColors[payment.status]}>
                    {paymentStatusTranslations[payment.status]}
                  </Badge>
                  <Badge>{payment.method}</Badge>
                </Group>
              );
            },
          },
          {
            accessor: 'packedBy',
            title: 'Zabalil',
            sortable: true,
          },
          {
            accessor: 'createdAt',
            title: 'Vytvorená',
            render: ({ createdAt, updatedAt }) => (
              <Stack gap={0}>
                <Text inherit>{dayjs(createdAt).format('DD.MM.YYYY HH:mm:ss')}</Text>
                {updatedAt > createdAt && (
                  <Text inherit c="dimmed" size="sm">
                    {dayjs(updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                  </Text>
                )}
              </Stack>
            ),
            sortable: true,
          },
          {
            accessor: 'invoice',
            title: 'Faktúra',
            textAlign: 'center',
            render: ({ id, invoice, customer, total }) =>
              invoice ? (
                <Anchor inherit href={invoice.url ?? ''} c={invoice.sent ? 'teal' : undefined}>
                  {invoice.number}
                </Anchor>
              ) : (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={() =>
                    openConfirmModal({
                      title: 'Vytvoriť faktúru',
                      children: (
                        <Stack>
                          <Alert color="orange">Naozaj chcete vytvoriť faktúru?</Alert>
                          <Text>
                            {customer.name} - {total} €
                          </Text>
                        </Stack>
                      ),
                      onConfirm: async () => {
                        await issueInvoice({ variables: { orderId: id, shouldSend: true } });
                      },
                    })
                  }
                >
                  <IconInvoice size={16} />
                </ActionIcon>
              ),
            sortable: true,
          },
          {
            accessor: 'actions',
            textAlign: 'right',
            title: (
              <ActionIcon size="sm" variant="subtle" ml="auto">
                <IconPlus size={16} />
              </ActionIcon>
            ),
            render: ({ id }) => (
              <Group>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="green"
                  onClick={() => {
                    void navigate(id);
                  }}
                >
                  <IconEye size={16} />
                </ActionIcon>
              </Group>
            ),
          },
        ]}
      />
    </Stack>
  );
};
